import { Instant } from "@js-joda/core";
import React, { useEffect, useState } from "react";
import useSWR from "swr";
import {
  Icd10Codes,
  PatientContractDiagnosisCode,
  PatientContractDiagnosisCodeId,
  PatientContractDiagnosisCodes,
  PatientContractDiagnosisSetParams,
  PatientContractId,
  UserId,
  VitalExtraDetails,
} from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import { withDefault } from "../../utils/withDefault";
import ErrorBottomSheet from "../ErrorBottomSheet/ErrorBottomSheet";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../Generic/AppCard";
import { If } from "../Generic/If";
import { isDefined } from "../Generic/IsDefined";
import WarningCard from "../WarningCard/WarningCard";
import MultiplePatientContractDiagnosisCodeActivity from "./MultiplePatientContractDiagnosisCodeActivity";
import {
  findEqualCode,
  groupDiagnoses,
  isEqualCodesLists,
  parseErrorResponse,
} from "./PatientContractDiagnosisCodeUtils";
import SinglePatientContractDiagnosisCodeActivity from "./SinglePatientContractDiagnosisCodeActivity";

interface Props {
  questionId: number;
  isDisabled: boolean;
  currentAnswer: PatientContractDiagnosisCode[] | null;
  getIcdCodeAutoCompleteOptions: (searchValue: string) => Promise<Icd10Codes>;
  getPatientContractDiagnosisCodes: () => Promise<PatientContractDiagnosisCodes>;
  onCreateDiagnosis: (
    params: PatientContractDiagnosisSetParams
  ) => Promise<PatientContractDiagnosisCodes>;
  onUpdateDiagnosis: (
    id: PatientContractDiagnosisCodeId,
    params: PatientContractDiagnosisSetParams
  ) => Promise<PatientContractDiagnosisCodes>;
  onDeleteDiagnosis: (id: PatientContractDiagnosisCodeId) => Promise<void>;
  onInsertAnswer: (
    id: number,
    answer: PatientContractDiagnosisCode[],
    vitalExtraDetails?: VitalExtraDetails
  ) => Promise<void>;
}

const PatientContractDiagnosisCodeActivities = ({
  questionId,
  isDisabled,
  currentAnswer,
  onInsertAnswer,
  getIcdCodeAutoCompleteOptions,
  getPatientContractDiagnosisCodes,
  onCreateDiagnosis,
  onUpdateDiagnosis,
  onDeleteDiagnosis,
}: Props) => {
  const [patientIcds, setPatientIcds] = useState<PatientContractDiagnosisCode[] | null>(null);

  const [principal, setPrincipal] = useState<PatientContractDiagnosisCode | null>(null);
  const [surgical, setSurgical] = useState<PatientContractDiagnosisCode | null>(null);
  const [others, setOthers] = useState<PatientContractDiagnosisCode[] | null>(null);
  const [error, setError] = useState<string>();

  const [syncIcds, setSyncIcds] = useState(
    HashMap.empty<PatientContractDiagnosisCodeId, boolean>()
  );

  const { mutate } = useSWR("diagnoses", getPatientContractDiagnosisCodes, {
    onSuccess: (data) => {
      setPatientIcds(data.patientContractDiagnosisCodes);
      return handleRefreshData(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- during eslint init
        isDefined(currentAnswer) ? currentAnswer : data.patientContractDiagnosisCodes ?? []
      );
    },
  });

  const hasAnswerCodeInProfile = React.useCallback(
    (code: PatientContractDiagnosisCode) => {
      if (!isDefined(patientIcds) || patientIcds.length === 0) {
        return false;
      }

      const foundMedication = findEqualCode(code, patientIcds);
      return foundMedication !== null;
    },
    [patientIcds]
  );

  const isProfileSync = React.useCallback(
    (code: PatientContractDiagnosisCode | null) => {
      if (isDefined(code)) {
        if (!syncIcds.has(code.id) && isDefined(patientIcds)) {
          syncIcds.set(code.id, hasAnswerCodeInProfile(code));
        }
        return syncIcds.get(code.id) ?? true;
      }

      return true;
    },
    [hasAnswerCodeInProfile, patientIcds, syncIcds]
  );

  const updateProfileSync = React.useCallback(
    (codeId: PatientContractDiagnosisCodeId, newValue: boolean) => {
      setSyncIcds(syncIcds.set(codeId, newValue));
    },
    [syncIcds]
  );

  const handleToggleProfileSync = React.useCallback(
    (code: PatientContractDiagnosisCode | null) => {
      if (isDefined(code)) {
        const isSync = syncIcds.get(code.id) ?? hasAnswerCodeInProfile(code);
        updateProfileSync(code.id, !isSync);
      }
    },
    [hasAnswerCodeInProfile, syncIcds, updateProfileSync]
  );

  const getCurrentDiagnosisCodes = React.useCallback(
    () => [principal, surgical, ...(others ?? [])].filter(isDefined),
    [others, principal, surgical]
  );

  const setDiagnoses = React.useCallback(
    (patientContractDiagnosisCodes?: PatientContractDiagnosisCode[] | null) => {
      const diagnoses = isDefined(patientContractDiagnosisCodes)
        ? patientContractDiagnosisCodes
        : isDefined(currentAnswer)
        ? currentAnswer
        : patientIcds ?? [];
      const currentAnswerGroup = groupDiagnoses(diagnoses);
      setPrincipal(currentAnswerGroup.principal);
      setSurgical(currentAnswerGroup.surgical);
      setOthers(currentAnswerGroup.others);
    },
    [currentAnswer, patientIcds]
  );

  const [areDifferentCodes, setDifferentCodes] = useState<boolean>(false);

  const handleRefreshData = React.useCallback(
    (patientContractDiagnosisCodes?: PatientContractDiagnosisCode[] | null) => {
      setDiagnoses(patientContractDiagnosisCodes);

      if (isDefined(currentAnswer) && isDefined(patientIcds)) {
        const isEqual = isEqualCodesLists(patientIcds, currentAnswer);
        setDifferentCodes(!isEqual);
      }
    },
    [currentAnswer, patientIcds, setDiagnoses]
  );

  useEffect(handleRefreshData, [handleRefreshData]);

  const setIcdCodesAnswer = React.useCallback(
    async (icdCodes: PatientContractDiagnosisCode[]) => {
      await onInsertAnswer(questionId, icdCodes);
    },
    [onInsertAnswer, questionId]
  );

  const resetAnswerDiagnosisCodes = React.useCallback(async () => {
    if (isDefined(patientIcds)) {
      await setIcdCodesAnswer(patientIcds);
      await mutate();
      setSyncIcds(HashMap.empty<PatientContractDiagnosisCodeId, boolean>());
    }
  }, [mutate, patientIcds, setIcdCodesAnswer]);

  const createAnswerDiagnosisCode = React.useCallback(
    (params: PatientContractDiagnosisSetParams) => {
      const minId = getCurrentDiagnosisCodes()
        .filter((code) => PatientContractDiagnosisCodeId.unwrap(code.id) < 0)
        .reduce(
          (min, code) =>
            PatientContractDiagnosisCodeId.unwrap(code.id) < min
              ? PatientContractDiagnosisCodeId.unwrap(code.id)
              : min,
          0
        );

      const nextAnswerDiagnosisId = minId - 1;

      const newCode: PatientContractDiagnosisCode = {
        id: PatientContractDiagnosisCodeId.wrap(nextAnswerDiagnosisId),
        isForBilling: false,
        createdAt: Instant.now(),
        createdBy: UserId.wrap(-1),
        updatedAt: null,
        updatedBy: null,
        patientContractId: PatientContractId.wrap(-1),
        ...params,
      } as unknown as PatientContractDiagnosisCode;

      return newCode;
    },
    [getCurrentDiagnosisCodes]
  );

  const addDiagnosisCodeToAnswer = React.useCallback(
    async (newCode: PatientContractDiagnosisCode) => {
      const icdCodes = [principal, surgical, ...(others ?? []), newCode].filter(isDefined);
      await setIcdCodesAnswer(icdCodes);
    },
    [others, principal, surgical, setIcdCodesAnswer]
  );

  const createDiagnosisOnPatient = React.useCallback(
    (newCode: PatientContractDiagnosisSetParams) => {
      return onCreateDiagnosis(newCode)
        .then((data) => {
          setPatientIcds(data.patientContractDiagnosisCodes);
          handleRefreshData(data.patientContractDiagnosisCodes);
          return data.patientContractDiagnosisCodes.filter((code) => code.id === data.createdId)[0];
        })
        .catch((err) => {
          setError(parseErrorResponse(err));
          return null;
        });
    },
    [handleRefreshData, onCreateDiagnosis]
  );

  const deleteDiagnosisCodeToAnswer = React.useCallback(
    async (id: PatientContractDiagnosisCodeId) => {
      const icdCodes = [principal, surgical, ...(others ?? [])]
        .filter(isDefined)
        .filter((code) => code.id !== id);

      await setIcdCodesAnswer(icdCodes);
    },
    [others, principal, surgical, setIcdCodesAnswer]
  );

  const deleteDiagnosisOnPatient = React.useCallback(
    async (id: PatientContractDiagnosisCodeId) => {
      await onDeleteDiagnosis(id);
      await mutate((oldData) => ({
        patientContractDiagnosisCodes: oldData.patientContractDiagnosisCodes.filter(
          (diagnosis) => diagnosis.id !== id
        ),
      }));
    },
    [mutate, onDeleteDiagnosis]
  );

  const updateDiagnosisOnPatient = React.useCallback(
    (id: PatientContractDiagnosisCodeId, changedCode: PatientContractDiagnosisCode) => {
      return onUpdateDiagnosis(id, changedCode).then((data) => {
        setPatientIcds(data.patientContractDiagnosisCodes);
      });
    },
    [onUpdateDiagnosis]
  );

  const updateDiagnosisCodeInAnswer = React.useCallback(
    async (id: PatientContractDiagnosisCodeId, changedCode: PatientContractDiagnosisCode) => {
      const icdCodes = [principal, surgical, ...(others ?? [])]
        .filter(isDefined)
        .map((code) =>
          code.id === id ? { ...code, ...changedCode, updatedAt: Instant.now() } : code
        );

      await setIcdCodesAnswer(icdCodes);
    },
    [others, principal, surgical, setIcdCodesAnswer]
  );

  const handleCreateDiagnosis = React.useCallback(
    async (params, synced: boolean) => {
      const created = synced
        ? await createDiagnosisOnPatient(params)
        : createAnswerDiagnosisCode(params);
      if (isDefined(created)) {
        await addDiagnosisCodeToAnswer(created);
      }

      await mutate();
    },
    [createDiagnosisOnPatient, createAnswerDiagnosisCode, mutate, addDiagnosisCodeToAnswer]
  );

  const handleUpdateDiagnosis = React.useCallback(
    async (id, changedCode, synced: boolean) => {
      if (synced) {
        if (id < 0) {
          const created = await createDiagnosisOnPatient({ ...changedCode, id: undefined });
          if (isDefined(created)) {
            changedCode.id = created.id;
          }
        } else {
          await updateDiagnosisOnPatient(id, changedCode);
        }
      }

      await updateDiagnosisCodeInAnswer(id, changedCode);

      await mutate();
    },
    [createDiagnosisOnPatient, mutate, updateDiagnosisCodeInAnswer, updateDiagnosisOnPatient]
  );

  const handleDeleteDiagnosis = React.useCallback(
    async (id, synced: boolean) => {
      if (synced) {
        await deleteDiagnosisOnPatient(id);
      }

      await deleteDiagnosisCodeToAnswer(id);

      await mutate();
    },
    [deleteDiagnosisCodeToAnswer, deleteDiagnosisOnPatient, mutate]
  );

  const handleRequestCloseErrorBottomSheet = React.useCallback(() => setError(undefined), []);

  return (
    <AppCard className="PatientContractDiagnosisCodeActivities">
      <AppCardHeader>
        <AppCardTitle>Diagnosis Codes</AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <If condition={areDifferentCodes}>
          <WarningCard
            message={translate(STRINGS.INFORMATION_NOT_MATCH_PROFILE)}
            actionTitle={translate(STRINGS.UPDATE_INFORMATION)}
            onAction={resetAnswerDiagnosisCodes}
            readonly={isDisabled}
          />
        </If>
        <div>
          <SinglePatientContractDiagnosisCodeActivity
            title={translate(STRINGS.PRINCIPAL_DIAGNOSIS)}
            type="PRINCIPAL"
            diagnosis={principal}
            isDisabled={isDisabled}
            searchIcd={getIcdCodeAutoCompleteOptions}
            onCreate={handleCreateDiagnosis}
            onUpdate={handleUpdateDiagnosis}
            onDelete={handleDeleteDiagnosis}
            isProfileSync={isProfileSync(principal)}
            onClickProfileSync={() => handleToggleProfileSync(principal)}
          />
          <SinglePatientContractDiagnosisCodeActivity
            title={translate(STRINGS.SURGICAL_PROCEDURE)}
            type="SURGICAL"
            diagnosis={surgical}
            isDisabled={isDisabled}
            searchIcd={getIcdCodeAutoCompleteOptions}
            onCreate={handleCreateDiagnosis}
            onUpdate={handleUpdateDiagnosis}
            onDelete={handleDeleteDiagnosis}
            isProfileSync={isProfileSync(surgical)}
            onClickProfileSync={() => handleToggleProfileSync(surgical)}
          />
          <MultiplePatientContractDiagnosisCodeActivity
            title={translate(STRINGS.OTHER_PERTINENT_DIAGNOSIS)}
            type={null}
            diagnoses={withDefault(others, [])}
            isDisabled={isDisabled}
            searchIcd={getIcdCodeAutoCompleteOptions}
            onCreate={handleCreateDiagnosis}
            onUpdate={handleUpdateDiagnosis}
            onDelete={handleDeleteDiagnosis}
            isProfileSync={isProfileSync}
            onClickProfileSync={handleToggleProfileSync}
          />
          <ErrorBottomSheet
            isOpen={error !== undefined}
            error={error}
            onRequestClose={handleRequestCloseErrorBottomSheet}
          />
        </div>
      </AppCardContent>
    </AppCard>
  );
};

export default React.memo(PatientContractDiagnosisCodeActivities);
